/**
 * Type-safe localStorage wrapper with built-in error handling
 * Eliminates repetitive try-catch blocks and provides better type safety
 */

export class TypedStorage {
  /**
   * Save a value to localStorage with JSON serialization
   * @param key - Storage key
   * @param value - Value to store (will be JSON serialized)
   * @returns true if successful, false if error occurred
   */
  static set<T>(key: string, value: T): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(value))
      return true
    } catch (error) {
      console.error(`Error saving to localStorage [${key}]:`, error)
      return false
    }
  }

  /**
   * Retrieve a value from localStorage with JSON deserialization
   * @param key - Storage key
   * @param defaultValue - Default value if key doesn't exist or error occurs
   * @returns The stored value or default value
   */
  static get<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key)
      if (item === null) {
        return defaultValue
      }
      return JSON.parse(item) as T
    } catch (error) {
      console.error(`Error loading from localStorage [${key}]:`, error)
      return defaultValue
    }
  }

  /**
   * Remove a value from localStorage
   * @param key - Storage key
   * @returns true if successful, false if error occurred
   */
  static remove(key: string): boolean {
    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error(`Error removing from localStorage [${key}]:`, error)
      return false
    }
  }

  /**
   * Clear all localStorage data
   * @returns true if successful, false if error occurred
   */
  static clear(): boolean {
    try {
      localStorage.clear()
      return true
    } catch (error) {
      console.error('Error clearing localStorage:', error)
      return false
    }
  }

  /**
   * Check if a key exists in localStorage
   * @param key - Storage key
   * @returns true if key exists, false otherwise
   */
  static has(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null
    } catch (error) {
      console.error(`Error checking localStorage [${key}]:`, error)
      return false
    }
  }
}
